export const beforeItems = [
  'You spend valuable time searching multiple references for information about testosterone therapy.',
  'You’re unsure which clinical considerations should be reviewed first.',
  'You need a more organized way to assess symptoms, laboratory findings, and relevant medical history.',
  'You want to keep safety considerations and monitoring requirements visible throughout treatment.',
  'You find it difficult to keep your TRT reference materials organized.',
  'You want a consistent framework for reviewing cases without relying entirely on memory.',
];

export const afterItems = [
  'You follow a structured reference covering the major stages of TRT care.',
  'You have an organized framework for reviewing potential candidates for evaluation.',
  'You can consult dedicated sections on laboratory assessment and interpretation.',
  'You have checklists to help organize safety considerations and follow-up.',
  'You can quickly locate relevant educational material without searching through scattered documents.',
  'You have a repeatable reference structure to support your clinical workflow.',
];

export const painPoints = [
  {
    title: 'You Spend Too Much Time Searching for Answers',
    text: 'A patient presents with symptoms potentially associated with testosterone deficiency, and you find yourself moving between multiple references to review the appropriate evaluation process.',
  },
  {
    title: 'You Want a Clearer Framework for Laboratory Assessment',
    text: 'You want to organize the relevant laboratory considerations and understand how results fit into the broader clinical picture rather than evaluating a number in isolation.',
  },
  {
    title: 'You Want to Review Safety Considerations More Systematically',
    text: 'You need an organized reference to help you review contraindications, precautions, relevant medical history, and monitoring considerations.',
  },
  {
    title: 'You Want to Approach Treatment Planning More Consistently',
    text: 'You want a structured way to organize treatment-related considerations, patient-specific factors, and follow-up planning.',
  },
  {
    title: 'You Need a Better Way to Organize Follow-Up',
    text: 'You want to keep monitoring considerations, symptom reassessment, laboratory review, and potential adverse effects visible throughout ongoing care.',
  },
  {
    title: 'You Want to Feel Better Prepared for TRT-Related Clinical Discussions',
    text: 'You want to review the relevant information before consultations and communicate more clearly with patients about the evaluation process, potential benefits, risks, and limitations of therapy.',
  },
];

export const resources = [
  {
    key: 'checklists',
    title: 'Clinical Evaluation Checklists',
    text: 'Structured prompts to help you organize the initial assessment, relevant history, diagnostic confirmation, and other important evaluation considerations.',
  },
  {
    key: 'worksheets',
    title: 'Laboratory Review Worksheets',
    text: 'Templates for recording relevant laboratory findings and organizing the questions they raise in the context of the broader clinical picture.',
  },
  {
    key: 'maps',
    title: 'Clinical Workflow Maps',
    text: 'Visual overviews of the major stages of TRT evaluation, treatment planning, and follow-up.',
  },
  {
    key: 'safety',
    title: 'Safety & Monitoring Templates',
    text: 'Organized reference sheets for documenting relevant safety considerations, monitoring plans, and follow-up questions.',
  },
  {
    key: 'docs',
    title: 'Consultation & Documentation Templates',
    text: 'Practical layouts for organizing patient discussions, clinical considerations, and follow-up planning.',
  },
] as const;

export const offerVolumes = [
  'Initial evaluation.',
  'Laboratory interpretation.',
  'Candidate assessment.',
  'Treatment planning.',
  'Safety and monitoring.',
  'Clinical workflow organization.',
];

export const offerBonuses = [
  'TRT Initial Assessment Checklist.',
  'Testosterone Lab Review Toolkit.',
  'TRT Safety & Monitoring Checklist Pack.',
  'TRT Patient Consultation Scriptbook.',
  'TRT Clinical Quick-Reference Pack.',
];

export const offerPerks = [
  'Instant digital access',
  'All six volumes',
  'All five bonuses',
  'Lifetime access to the purchased materials',
  '7-day money-back guarantee',
];

export const guaranteeItems = [
  'Explore all six volumes.',
  'Review the educational checklists.',
  'Examine the laboratory worksheets.',
  'Explore the clinical workflow maps.',
  'Review the consultation and monitoring templates.',
];

export const testimonials = [
  {
    name: 'Sofia Ramirez, MD',
    role: 'Internal Medicine',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a5dd1fa1-e25e-4bf8-b579-33fdfdbfdbe8.jpg',
    quote:
      'The most useful part for me was having the main stages of TRT evaluation and monitoring organized in one place. It gives me a convenient reference to revisit when I need it.',
  },
  {
    name: 'Dr. Robert Hale',
    role: 'Family Medicine',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/46bc3d08-52cd-494e-beb4-d1f775cb7dbf.jpg',
    quote:
      'I appreciated the way the material separates the evaluation, laboratory review, and monitoring considerations. It’s easier to find the section I need instead of searching through multiple documents.',
  },
  {
    name: 'Dr. Marcus Bennett',
    role: 'General Practitioner',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/31c350c5-1dcc-4cef-b81a-64f7a45d9535.jpg',
    quote:
      'The checklists and visual workflow are useful additions to the main reference. They help me organize the questions I want to review during my clinical preparation.',
  },
  {
    name: 'Emily Carter, NP',
    role: 'Nurse Practitioner, Men’s Health',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/b94a395b-8dcc-4b28-9fe8-4f8a39f0a144.jpg',
    quote:
      'The collection brings together several topics that I previously kept in separate notes. I like having a more structured educational reference.',
  },
  {
    name: 'Dr. Arjun Mehta',
    role: 'Endocrinology',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/a0d34fec-c24b-4cc3-b6e6-31f3342f0106.jpg',
    quote:
      'I purchased it for the workflow maps and laboratory review material. The format makes the content easier to revisit when I need a quick overview.',
  },
];

export const faqs = [
  {
    q: 'How will I receive TRT Blueprint?',
    a: 'Immediately after your payment is confirmed, you’ll receive instructions to access and download the digital materials. The collection is designed for convenient use on a computer, tablet, or compatible mobile device.',
  },
  {
    q: 'Is TRT Blueprint suitable for healthcare professionals in different countries?',
    a: 'The collection is intended as a general educational reference. Clinical guidelines, prescribing requirements, approved formulations, and regulations differ by country. You should always consult the current guidance and prescribing information applicable to your jurisdiction.',
  },
  {
    q: 'Do I need to be an endocrinologist or urologist?',
    a: 'The material is designed for qualified healthcare professionals who want an organized educational reference on testosterone therapy. It does not replace specialist training, professional scope-of-practice requirements, or referral when clinically indicated.',
  },
  {
    q: 'Does TRT Blueprint tell me exactly how to prescribe testosterone?',
    a: 'TRT Blueprint is an educational reference designed to organize the evaluation, treatment-planning, safety, and monitoring considerations associated with TRT. It should not be used as a substitute for individualized clinical assessment, current guidelines, or product-specific prescribing information.',
  },
  {
    q: 'Are the materials downloadable?',
    a: 'Yes. The product is delivered digitally, allowing you to download and revisit the purchased files.',
  },
  {
    q: 'Is this a subscription?',
    a: 'No. The offer is a one-time payment of $27 USD, with no recurring monthly charge.',
  },
  {
    q: 'Will I receive future updates?',
    a: 'The offer includes the materials available at the time of purchase.',
  },
  {
    q: 'What if TRT Blueprint isn’t right for me?',
    a: 'Your purchase includes a 7-day money-back guarantee. Contact customer support within seven days of purchase to request a refund under the stated terms.',
  },
  {
    q: 'Is TRT Blueprint intended for patients who want to start testosterone therapy?',
    a: 'No. The collection is designed as an educational resource for healthcare professionals. Patients should discuss symptoms, testing, treatment options, and potential risks with a qualified healthcare professional.',
  },
];

export const finalPerks = [
  'TRT Blueprint — 6 premium volumes',
  '5 exclusive educational bonuses',
  'Clinical checklists and worksheets',
  'Visual workflow maps and reference materials',
  'Instant digital access',
  'Lifetime access to purchased materials',
  '7-day money-back guarantee',
];

export const recentBuyers = [
  { name: 'Dr. Alejandro M.', country: 'Mexico', minutes: 2 },
  { name: 'Dra. Valentina R.', country: 'Argentina', minutes: 4 },
  { name: 'Dr. Santiago P.', country: 'Colombia', minutes: 1 },
  { name: 'Dra. Camila F.', country: 'Chile', minutes: 6 },
  { name: 'Dr. Javier L.', country: 'Spain', minutes: 3 },
  { name: 'Dr. Mateo G.', country: 'Peru', minutes: 7 },
  { name: 'Dra. Lucía H.', country: 'Uruguay', minutes: 5 },
  { name: 'Dr. Andrés V.', country: 'Ecuador', minutes: 2 },
  { name: 'Dra. Isabella C.', country: 'Costa Rica', minutes: 8 },
  { name: 'Dr. Diego S.', country: 'Dominican Republic', minutes: 3 },
  { name: 'Dra. Mariana T.', country: 'Panama', minutes: 4 },
  { name: 'Dr. Sebastián R.', country: 'Puerto Rico', minutes: 6 },
];
